import React, { useMemo, useState } from "react";
import { fmtDate } from "../lib/format";
import { Button } from "./ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

const STATUS_DOT = {
  scheduled: "bg-secondary-foreground/40", confirmed: "bg-primary",
  completed: "bg-[hsl(var(--success))]", cancelled: "bg-destructive",
  no_show: "bg-muted-foreground", pending_deposit: "bg-accent",
};
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function startOfWeek(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // Mon=0
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }

export default function WeekCalendar({ appointments, workStart = 9, workEnd = 18, onReschedule, onSelect }) {
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [dragOver, setDragOver] = useState(null);

  const times = useMemo(() => {
    const t = [];
    for (let m = workStart * 60; m < workEnd * 60; m += 30) t.push(`${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`);
    return t;
  }, [workStart, workEnd]);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const todayStr = fmtDate(new Date());

  const map = useMemo(() => {
    const m = {};
    appointments.forEach((a) => {
      if (a.status === "cancelled") return;
      const rounded = a.start_time.slice(0, 5);
      m[`${a.date}|${rounded}`] = m[`${a.date}|${rounded}`] || [];
      m[`${a.date}|${rounded}`].push(a);
    });
    return m;
  }, [appointments]);

  const onDrop = (e, dateStr, time) => {
    e.preventDefault();
    setDragOver(null);
    const id = e.dataTransfer.getData("text/plain");
    if (id) onReschedule(id, dateStr, time);
  };

  const label = `${weekStart.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${addDays(weekStart, 6).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="font-medium">{label}</div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" data-testid="week-prev" onClick={() => setWeekStart(addDays(weekStart, -7))}><ChevronLeft size={15} /></Button>
          <Button variant="outline" size="sm" data-testid="week-today" onClick={() => setWeekStart(startOfWeek(new Date()))}>Today</Button>
          <Button variant="outline" size="sm" data-testid="week-next" onClick={() => setWeekStart(addDays(weekStart, 7))}><ChevronRight size={15} /></Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <div className="min-w-[820px]">
          <div className="grid" style={{ gridTemplateColumns: "64px repeat(7, 1fr)" }}>
            <div className="border-b border-r border-border bg-secondary/40" />
            {days.map((d, i) => {
              const ds = fmtDate(d);
              return (
                <div key={i} className={`border-b border-r border-border px-2 py-2 text-center ${ds === todayStr ? "bg-accent/10" : "bg-secondary/40"}`}>
                  <div className="text-xs text-muted-foreground">{DAYS[i]}</div>
                  <div className={`text-sm font-semibold ${ds === todayStr ? "text-accent" : ""}`}>{d.getDate()}</div>
                </div>
              );
            })}
            {times.map((t) => (
              <React.Fragment key={t}>
                <div className="border-b border-r border-border px-2 py-1 text-xs text-muted-foreground font-mono text-right">{t}</div>
                {days.map((d, i) => {
                  const ds = fmtDate(d);
                  const key = `${ds}|${t}`;
                  const cellAppts = map[key] || [];
                  const isOver = dragOver === key;
                  return (
                    <div key={i} data-testid={`cell-${ds}-${t}`}
                      onDragOver={(e) => { e.preventDefault(); setDragOver(key); }}
                      onDragLeave={() => setDragOver((k) => (k === key ? null : k))}
                      onDrop={(e) => onDrop(e, ds, t)}
                      className={`border-b border-r border-border min-h-[42px] p-1 transition-colors ${isOver ? "bg-accent/20" : ""}`}>
                      {cellAppts.map((a) => (
                        <div key={a.id} draggable
                          onDragStart={(e) => e.dataTransfer.setData("text/plain", a.id)}
                          onClick={() => onSelect && onSelect(a)}
                          data-testid={`week-appt-${a.id}`}
                          className="mb-1 rounded-md border border-border bg-background px-2 py-1 cursor-grab active:cursor-grabbing hover:border-accent transition-colors">
                          <div className="flex items-center gap-1.5">
                            <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[a.status] || "bg-muted-foreground"}`} />
                            <span className="text-xs font-medium truncate">{a.client_name}</span>
                          </div>
                          <div className="text-[10px] text-muted-foreground truncate">{a.service_name}</div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
      <div className="px-4 py-2 border-t border-border text-xs text-muted-foreground">Drag any appointment to a new day or time to reschedule.</div>
    </div>
  );
}
